import { RequireRole, useCurrentOrg, useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

interface OrgSettings {
  id: string;
  name: string;
  slug: string;
  planTier: string;
  logoUrl: string | null;
  brandLogoUrl: string | null;
  brandPrimaryColor: string | null;
  brandEmailFromName: string | null;
}

export function SettingsOrganizationRoute() {
  const org = useCurrentOrg();
  const getToken = useSessionTokenGetter();

  const [settings, setSettings] = useState<OrgSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [brandLogoUrl, setBrandLogoUrl] = useState('');
  const [brandPrimaryColor, setBrandPrimaryColor] = useState('#1E3A5F');
  const [brandEmailFromName, setBrandEmailFromName] = useState('');

  const orgId = org?.clerkOrgId ?? null;
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/settings/organization`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const json = (await res.json()) as { organization: OrgSettings };
        if (!cancelled) {
          setSettings(json.organization);
          setBrandLogoUrl(json.organization.brandLogoUrl ?? '');
          setBrandPrimaryColor(json.organization.brandPrimaryColor ?? '#1E3A5F');
          setBrandEmailFromName(json.organization.brandEmailFromName ?? '');
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, getToken]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/settings/organization`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          brandLogoUrl: brandLogoUrl || null,
          brandPrimaryColor: brandPrimaryColor || null,
          brandEmailFromName: brandEmailFromName || null,
        }),
      });
      if (!res.ok) {
        const json = (await res.json()) as { error?: string };
        throw new Error(json.error ?? `${res.status}`);
      }
      const json = (await res.json()) as { organization: OrgSettings };
      setSettings(json.organization);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Organization Settings</h2>
        <p className="mt-1 text-sm text-slate-500">
          Configure your EDC's profile, branding, and defaults.
        </p>
      </div>

      <RequireRole
        requiredRole="owner"
        fallback={
          <div className="rounded-lg border border-slate-200 bg-amber-50 p-4 text-sm text-amber-900">
            Organization settings are visible to Owners only.
          </div>
        }
      >
        {/* Profile section */}
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Profile</h3>
          {loading ? (
            <p className="mt-4 text-sm text-slate-400">Loading…</p>
          ) : (
            <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-slate-500">Name</dt>
                <dd className="mt-0.5 font-medium">{settings?.name ?? org?.name ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Slug</dt>
                <dd className="mt-0.5 font-medium">{settings?.slug ?? org?.slug ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Plan</dt>
                <dd className="mt-0.5 font-medium capitalize">{settings?.planTier ?? '—'}</dd>
              </div>
            </dl>
          )}
        </div>

        {/* Brand settings form */}
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
            White-Label Branding
          </h3>
          <form onSubmit={(e) => void handleSave(e)} className="space-y-5">
            <div>
              <label
                htmlFor="brandLogoUrl"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Brand Logo URL
              </label>
              <input
                id="brandLogoUrl"
                type="url"
                value={brandLogoUrl}
                onChange={(e) => setBrandLogoUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="mt-1 text-xs text-slate-400">
                Shown in applicant portal and email headers.
              </p>
            </div>

            <div>
              <label
                htmlFor="brandPrimaryColor"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Brand Primary Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  id="brandPrimaryColor"
                  type="color"
                  value={brandPrimaryColor}
                  onChange={(e) => setBrandPrimaryColor(e.target.value)}
                  className="h-10 w-14 cursor-pointer rounded border border-slate-300 p-0.5"
                />
                <input
                  type="text"
                  value={brandPrimaryColor}
                  onChange={(e) => setBrandPrimaryColor(e.target.value)}
                  placeholder="#1E3A5F"
                  className="w-32 rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <p className="mt-1 text-xs text-slate-400">
                Hex color used in the applicant portal header.
              </p>
            </div>

            <div>
              <label
                htmlFor="brandEmailFromName"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Email From Name
              </label>
              <input
                id="brandEmailFromName"
                type="text"
                value={brandEmailFromName}
                onChange={(e) => setBrandEmailFromName(e.target.value)}
                placeholder="My EDC Name"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="mt-1 text-xs text-slate-400">
                Display name in applicant emails (e.g., "Hays EDC").
              </p>
            </div>

            {/* Live preview */}
            {(brandLogoUrl || brandPrimaryColor) && (
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div
                  className="flex items-center gap-3 px-4 py-3"
                  style={{ backgroundColor: brandPrimaryColor || '#1E3A5F' }}
                >
                  {brandLogoUrl && (
                    <img
                      src={brandLogoUrl}
                      alt="Logo preview"
                      className="h-8 w-auto object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  )}
                  <span className="text-sm font-semibold text-white">
                    {brandEmailFromName || org?.name || 'Your EDC'}
                  </span>
                </div>
                <div className="bg-white px-4 py-3">
                  <p className="text-xs text-slate-400">Portal header preview</p>
                </div>
              </div>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}
            {saved && <p className="text-sm text-green-600">Branding saved successfully.</p>}

            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save branding'}
            </button>
          </form>
        </div>
      </RequireRole>
    </div>
  );
}
